import React, { useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, Modal } from 'react-native';
import WebScrollView from '../WebScrollView';
import { X } from '../../lib/icons';
import { createShadow } from '../../lib/styles';
import { useMobileContainer } from '../MobileContainer';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  height?: 'half' | 'full' | 'auto';
}

const BottomSheet: React.FC<BottomSheetProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  height = 'half' 
}) => {
  const { isInMobileContainer, isDesktop } = useMobileContainer();

  useEffect(() => {
    // Only manipulate document.body on web
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }

      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  const getHeightStyle = () => {
    switch (height) {
      case 'full': return { height: '90%' as any };
      case 'half': return { height: '60%' as any };
      case 'auto': return { maxHeight: '80%' as any };
      default: return { height: '60%' as any };
    }
  };

  if (!isOpen) return null;

  // If we're in a mobile container on desktop, use a centered modal-style layout
  const useContainedStyle = isInMobileContainer && isDesktop;

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={[styles.overlay, useContainedStyle && styles.overlayContained]}>
        <Pressable 
          style={[styles.backdrop, useContainedStyle && styles.backdropContained]} 
          onPress={onClose} 
        />
        
        <View style={[
          styles.sheet, 
          getHeightStyle(),
          useContainedStyle && styles.sheetContained
        ]}>
          {/* Handle */}
          <View style={styles.handle}>
            <View style={styles.handleBar} />
          </View>
          
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <Pressable
              onPress={onClose}
              style={styles.closeButton}
            >
              <X size={20} color="#9ca3af" />
            </Pressable>
          </View>
          
          {/* Content */}
          <WebScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
            {children}
          </WebScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  overlayContained: {
    ...(Platform.OS === 'web' && {
      alignItems: 'center',
      justifyContent: 'center',
    }),
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  backdropContained: {
    ...(Platform.OS === 'web' && {
      maxWidth: 600,
      left: '50%',
      transform: 'translateX(-50%)' as any,
    }),
  },
  sheet: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    ...(Platform.OS === 'web' && createShadow(0, -2, 0.3, 8, 5)),
  },
  sheetContained: {
    ...(Platform.OS === 'web' && {
      maxWidth: 600,
      width: '100%',
      borderRadius: 16,
      marginBottom: 0,
    }),
  },
  handle: {
    alignItems: 'center',
    paddingTop: 12,
    paddingBottom: 8,
  },
  handleBar: {
    width: 48,
    height: 4,
    backgroundColor: '#d1d5db',
    borderRadius: 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  closeButton: {
    padding: 8,
    borderRadius: 20,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  content: {
    flex: 1,
    minHeight: 0,
  },
  contentContainer: {
    paddingLeft: 32,
    paddingRight: 32,
    paddingTop: 16,
    paddingBottom: 24,
  },
});

export default BottomSheet;
